<template>
  <div
    class="fixed top-[66px] right-4 z-50 max-w-sm w-full n-raised border rounded-(--nviya-radius-lg) transform transition-all duration-300 ease-in-out"
    :class="{
      'translate-x-0 opacity-100': show,
      'translate-x-full opacity-0': !show,
      'cursor-pointer hover:shadow-(--nviya-shadow-lg)': notification.url
      }"
    @click="handleNotificationClick"
  >
    <div class="p-4">
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <div 
            class="w-8 h-8 rounded-full flex items-center justify-center"
            :class="{
              'bg-blue-100': notification.type === 'news',
              'bg-red-100': notification.type === 'alert',
              'bg-(--nviya-figure-bg)': notification.type === 'system',
              'bg-emerald-100': notification.type === 'client_pack' && notification.tone !== 'critical',
              'bg-(--nviya-critical-bg)': notification.tone === 'critical',
              'bg-blue-100': !notification.type && notification.type !== 'client_pack'
              }"
          >
            <FontAwesome 
              :icon="getNotificationIcon(notification.type)" 
              class="w-4 h-4"
              :class="{
                'text-blue-600': notification.type === 'news' || (!notification.type && notification.type !== 'client_pack'),
                'text-red-600': notification.type === 'alert',
                'text-gray-600': notification.type === 'system',
                'text-emerald-600': notification.type === 'client_pack' && notification.tone !== 'critical',
                'text-(--nviya-critical-text)': notification.tone === 'critical'
                }"
            />
          </div>
        </div>
        <div class="ml-3 flex-1">
          <div class="flex items-center space-x-2">
            <h4 class="text-sm font-semibold n-heading">{{ notification.title }}</h4>
            <span 
              v-if="notification.type === 'news'"
              class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
            >
              News
            </span>
          </div>
          <p class="mt-1 text-sm text-gray-600">{{ notification.description }}</p>
          <div v-if="notification.action" class="mt-3 flex items-center gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold bg-(--nviya-blue) text-(--nviya-on-blue) hover:bg-(--nviya-blue-hover) transition-colors"
              @click.stop="handleAction"
            >
              <FontAwesome v-if="notification.action.kind === 'client_pack_download'" :icon="['fas', 'download']" class="w-3.5 h-3.5" />
              {{ notification.action.label }}
            </button>
          </div>
          <div class="mt-1 flex items-center justify-between">
            <p class="text-xs text-gray-400">{{ formatTime(notification.timestamp) }}</p>
            <p 
              v-if="notification.risk && notification.type === 'news'"
              class="text-xs font-medium text-orange-600"
            >
              {{ notification.risk }}
            </p>
          </div>
        </div>
        <div class="ml-4 flex-shrink-0">
          <button
            @click="$emit('close')"
            class="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
          >
            <FontAwesome :icon="['fas', 'times']" class="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
    
    <!-- Progress bar -->
    <div v-if="!notification.persistent" class="h-1 bg-(--nviya-figure-bg) rounded-b-lg overflow-hidden">
      <div
        class="h-full bg-blue-600 transition-all duration-75 ease-linear"
        :style="{ width: `${progress}%` }"
      ></div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  notification: {
    type: Object,
    required: true
  },
  show: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['close', 'navigate', 'action'])

const handleAction = () => {
  emit('action', props.notification)
  emit('close')
}

const progress = ref(100)

// Get appropriate icon for notification type
const getNotificationIcon = (type) => {
  switch (type) {
    case 'news':
      return ['fas', 'newspaper']
    case 'alert':
      return ['fas', 'exclamation-triangle']
    case 'system':
      return ['fas', 'cog']
    case 'client_pack':
      return props.notification.tone === 'critical' ? ['fas', 'exclamation-circle'] : ['fas', 'file-powerpoint']
    default:
      return ['fas', 'bell']
  }
}

// Handle notification click
const handleNotificationClick = (event) => {
  // Don't navigate if clicking the close button
  const target = event.target
  if (target.closest('button')) {
    return
  }
  
  if (props.notification.url) {
    emit('navigate', props.notification.url)
    emit('close')
  }
}

// Format timestamp to relative time
const formatTime = (timestamp) => {
  const now = new Date()
  const diff = now.getTime() - timestamp.getTime()
  const minutes = Math.floor(diff / 60000)
  
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

// Auto-dismiss countdown
let interval = null

const startCountdown = () => {
  if (interval) clearInterval(interval)
  
  progress.value = 100
  const duration = 5000 // 5 seconds
  const updateInterval = 50 // Update every 50ms
  const decrement = (100 / duration) * updateInterval
  
  interval = setInterval(() => {
    progress.value -= decrement
    if (progress.value <= 0) {
      progress.value = 0
      if (interval) clearInterval(interval)
      emit('close')
    }
  }, updateInterval)
}

// Start countdown when component shows
watch(() => props.show, (newShow) => {
  // A toast with an action stays until the reader acts or closes it —
  // a Download button that vanishes after five seconds is not a button.
  if (newShow && props.notification.persistent) {
    progress.value = 100
  } else if (newShow) {
    startCountdown()
  } else if (interval) {
    clearInterval(interval)
  }
}, { immediate: true })

onUnmounted(() => {
  if (interval) clearInterval(interval)
})
</script>

<style scoped>
.transition-all {
  transition-property: transform, opacity;
}
</style>
